import { PropsWithChildren, ReactElement } from 'react'
import {
  createStyles,
  makeStyles,
  CssBaseline,
  Grid,
  Theme
} from '@material-ui/core'
import { Routes, Route, Navigate } from 'react-router-dom'
import { isMobileOnly as isMobile } from 'react-device-detect'
import { PagerProvider } from './pager'
import { StateLoader } from './debug'
import NoMatchPage from './NoMatchPage'
import SkipToLinks from './SkipToLinks'
import ScrollTop from './ScrollTop'
import Menu, { backPages, drawerSectionsForYear } from './Menu'
import { Section, SectionItem } from './ResponsiveDrawer'
import Header from './Header'
import StepProgress from './StepProgress'
import ChatWidget from './ChatWidget'
import LoginPage from './LoginPage'
import OAuthCallback from 'ustaxes/auth/OAuthCallback'

import Urls from 'ustaxes/data/urls'
import DataPropagator from './DataPropagator'
import YearStatusBar from './YearStatusBar'
import { useSelector } from 'react-redux'
import { TaxYear } from 'ustaxes/core/data'
import { YearsTaxesState } from 'ustaxes/redux'
import { useAuth } from 'ustaxes/auth/AuthContext'

type Props = {
  isMobile: boolean
}

const useStyles = makeStyles<Theme, Props>((theme: Theme) =>
  createStyles({
    container: {
      display: 'flex',
      minHeight: '100vh'
    },
    content: ({ isMobile }) => ({
      padding: theme.spacing(3),
      backgroundColor: '#FFFFFF',
      [theme.breakpoints.up('sm')]: {
        borderRadius: 8,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        margin: theme.spacing(1.5),
        padding: theme.spacing(3),
        border: '1px solid rgba(0,0,0,0.06)'
      },
      width: isMobile ? '100%' : undefined
    }),
    // necessary for content to be below app bar
    toolbar: {
      ...theme.mixins.toolbar,
      [theme.breakpoints.up('sm')]: {
        display: 'none'
      }
    }
  })
)

export default function Main(): ReactElement {
  const activeYear: TaxYear = useSelector(
    (state: YearsTaxesState) => state.activeYear
  )
  const { user, signOut } = useAuth()

  const classes = useStyles({ isMobile })

  const steps: SectionItem[] = drawerSectionsForYear(activeYear).flatMap(
    (section: Section) => section.items
  )

  const allItems: SectionItem[] = [...steps, ...backPages]

  const Layout = ({
    showMenu = true,
    showStepper = true,
    children
  }: PropsWithChildren<{
    showMenu?: boolean
    showStepper?: boolean
  }>) => (
    <>
      <Header user={user} onSignOut={() => void signOut()} />
      {showStepper && showMenu ? <StepProgress /> : undefined}
      {showMenu ? <Menu /> : undefined}
      <Grid
        component="main"
        tabIndex={-1}
        container
        justifyContent="center"
        direction="row"
        style={{
          marginTop: showStepper && showMenu ? 136 : 72,
          marginLeft: showMenu && !isMobile ? 260 : 0
        }}
      >
        <Grid item sm={12} md={8} lg={6}>
          {showMenu ? (
            <Grid item className={classes.content}>
              <YearStatusBar />
            </Grid>
          ) : undefined}
          <Grid item className={classes.content}>
            {isMobile && showMenu ? (
              <div className={classes.toolbar} />
            ) : undefined}
            {children}
          </Grid>
        </Grid>
      </Grid>
    </>
  )

  return (
    <>
      <CssBaseline />
      <SkipToLinks />
      <div className={classes.container}>
        <StateLoader />
        <PagerProvider pages={steps}>
          <Routes>
            {/* Auth routes - rendered without layout */}
            <Route path={Urls.login} element={<LoginPage />} />
            <Route
              path="/auth/callback/:provider"
              element={<OAuthCallback />}
            />

            {/* Default redirect */}
            <Route path="/" element={<Navigate to={Urls.default} />} />

            {/* App routes */}
            {allItems.map((item) => (
              <Route
                key={item.title}
                path={item.url}
                element={
                  <Layout
                    showMenu={!item.url.includes('start')}
                    showStepper={!item.url.includes('start')}
                  >
                    <DataPropagator />
                    {item.element}
                  </Layout>
                }
              />
            ))}
            <Route
              path="*"
              element={
                <Layout>
                  <NoMatchPage />
                </Layout>
              }
            />
          </Routes>
        </PagerProvider>
        {!isMobile && <ScrollTop />}
        <ChatWidget />
      </div>
    </>
  )
}
